import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { zodiacSigns } from "@/data/zodiac";

const DISMISSED_KEY = "nebula_popup_dismissed";

const ShootingStar = ({ delay }: { delay: number }) => (
  <motion.div
    className="absolute w-1 h-1 bg-accent rounded-full"
    initial={{ x: -20, y: -20, opacity: 0 }}
    animate={{
      x: [0, 200, 300],
      y: [0, 100, 180],
      opacity: [0, 1, 0],
    }}
    transition={{ duration: 1.5, delay, repeat: Infinity, repeatDelay: 3 + delay }}
    style={{ top: `${10 + delay * 15}%`, left: `${5 + delay * 10}%` }}
  >
    <div className="w-8 h-[1px] bg-gradient-to-r from-accent to-transparent -rotate-45 origin-left" />
  </motion.div>
);

type Step = "zodiac" | "email" | "thanks";

const CosmicEmailPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>("zodiac");
  const [selectedSign, setSelectedSign] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (!dismissed) {
      const timer = setTimeout(() => setIsOpen(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setIsOpen(false);
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
  };

  const handleSignSelect = (signName: string) => {
    setSelectedSign(signName);
    setStep("email");
  };

  const sendEmails = async (subscriberEmail: string) => {
    const subscriptionId = crypto.randomUUID();

    supabase.functions.invoke("send-transactional-email", {
      body: {
        templateName: "cosmic-welcome",
        recipientEmail: subscriberEmail,
        idempotencyKey: `cosmic-welcome-${subscriptionId}`,
      },
    }).catch((err) => console.error("Welcome email failed:", err));

    try {
      const { data: aiData } = await supabase.functions.invoke("astrology-ai", {
        body: {
          tool: "horoscope",
          params: { sign: selectedSign || "general" },
        },
      });

      const today = new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      let horoscopeText = "The universe has a special message waiting for you today. Visit Nebula Cosmic to unlock your full reading.";
      if (aiData?.result) {
        try {
          const cleaned = (aiData.result as string).replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
          const parsed = JSON.parse(cleaned);
          horoscopeText = `💕 Love: ${parsed.love}\n\n💼 Career: ${parsed.career}\n\n🌙 Mood: ${parsed.mood}\n\n⚡ Energy: ${parsed.energy}`;
        } catch {
          horoscopeText = aiData.result as string;
        }
      }

      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "daily-horoscope",
          recipientEmail: subscriberEmail,
          idempotencyKey: `daily-horoscope-${subscriptionId}`,
          templateData: { horoscope: horoscopeText, date: today },
        },
      });
    } catch (err) {
      console.error("Horoscope email failed:", err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { error: dbError } = await supabase
        .from("email_subscribers")
        .insert({ email: email.trim(), source: `homepage_popup_${selectedSign.toLowerCase() || "unknown"}` });

      if (dbError) {
        if (dbError.code === "23505") {
          setStep("thanks");
        } else {
          setError("Something went wrong. Please try again.");
          setLoading(false);
          return;
        }
      } else {
        setStep("thanks");
        sendEmails(email.trim());
      }

      localStorage.setItem(DISMISSED_KEY, Date.now().toString());
      setTimeout(() => setIsOpen(false), 5000);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedZodiac = zodiacSigns.find((z) => z.name === selectedSign);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleDismiss} />

          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 30 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="relative w-full max-w-md rounded-2xl border border-primary/30 bg-card overflow-hidden max-h-[90vh] overflow-y-auto"
            style={{
              boxShadow: "0 0 60px hsl(270 60% 60% / 0.3), 0 0 120px hsl(270 80% 70% / 0.15)",
            }}
          >
            <ShootingStar delay={0} />
            <ShootingStar delay={1.2} />
            <ShootingStar delay={2.4} />

            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-primary/20 to-transparent pointer-events-none" />

            <button
              onClick={handleDismiss}
              aria-label="Close popup"
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-secondary/80 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative z-10 p-6 sm:p-8 text-center">
              <AnimatePresence mode="wait">
                {/* STEP 1: Zodiac Selection */}
                {step === "zodiac" && (
                  <motion.div
                    key="zodiac"
                    initial={{ opacity: 0, x: 0 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.25 }}
                  >
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 mb-4"
                    >
                      <Sparkles className="w-7 h-7 text-accent" />
                    </motion.div>

                    <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground mb-1.5">
                      ✨ Discover Your Cosmic Reading
                    </h3>
                    <p className="text-muted-foreground text-sm mb-5 leading-relaxed">
                      Select your zodiac sign to unlock today's personalised horoscope.
                    </p>

                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {zodiacSigns.map((sign) => (
                        <motion.button
                          key={sign.name}
                          whileHover={{ scale: 1.08 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleSignSelect(sign.name)}
                          className="flex flex-col items-center gap-1 p-2.5 rounded-xl border border-border/50 bg-secondary/30 hover:bg-primary/20 hover:border-primary/50 transition-colors cursor-pointer"
                        >
                          <span className="text-2xl leading-none">{sign.symbol}</span>
                          <span className="text-[11px] text-muted-foreground font-medium">{sign.name}</span>
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* STEP 2: Email Capture */}
                {step === "email" && (
                  <motion.div
                    key="email"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.25 }}
                  >
                    {/* Back button */}
                    <button
                      onClick={() => setStep("zodiac")}
                      className="absolute top-3 left-3 z-10 p-1.5 rounded-full bg-secondary/80 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors text-xs flex items-center gap-1"
                    >
                      ← Back
                    </button>

                    {selectedZodiac && (
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 mb-4">
                        <span className="text-3xl">{selectedZodiac.symbol}</span>
                      </div>
                    )}

                    <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground mb-1.5">
                      🔮 Your Cosmic Reading Is Ready
                    </h3>
                    <p className="text-muted-foreground text-sm mb-5 leading-relaxed">
                      Enter your email to reveal today's personalised horoscope including insights for love, career, mood, and energy.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-3">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email to reveal your horoscope"
                        required
                        className="w-full rounded-xl border border-border bg-secondary/50 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      {error && <p className="text-destructive text-xs">{error}</p>}
                      <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full rounded-xl bg-gradient-to-r from-primary to-[hsl(var(--cosmic-glow))] text-primary-foreground font-semibold py-3 text-sm transition-all disabled:opacity-50 relative overflow-hidden"
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          animate={{ x: ["-100%", "200%"] }}
                          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                        />
                        <span className="relative flex items-center justify-center gap-2">
                          <Star className="w-4 h-4" />
                          {loading ? "Subscribing…" : "✨ Reveal My Horoscope"}
                        </span>
                      </motion.button>
                    </form>

                    <p className="text-[11px] text-muted-foreground mt-3">
                      🌙 Join thousands discovering their cosmic guidance — no spam, unsubscribe anytime.
                    </p>
                  </motion.div>
                )}

                {/* STEP 3: Thank You */}
                {step === "thanks" && (
                  <motion.div
                    key="thanks"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/20 mb-5">
                      <Sparkles className="w-8 h-8 text-accent" />
                    </div>
                    <h3 className="font-display text-2xl font-bold text-foreground mb-2">
                      ✨ Thank You! ✨
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Check your inbox for your {selectedSign} cosmic insight. Your journey through the stars begins now!
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CosmicEmailPopup;
